import { CheckCircle2, Loader2 } from "lucide-react";

import { DoctorStatusBadge } from "@/components/common/status-badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { formatDate, initials } from "@/lib/format";
import type { Doctor } from "@/types/doctor";

export function ApproveDoctorDialog({
  doctor,
  open,
  onOpenChange,
  onConfirm,
  pending,
}: {
  doctor: Doctor | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  pending: boolean;
}) {
  if (!doctor) return null;

  const rows: Array<[string, string]> = [
    ["Specialization", doctor.specialization],
    ["Hospital", doctor.hospitalName ?? "Not provided"],
    ["Experience", `${doctor.experience} ${doctor.experience === 1 ? "year" : "years"}`],
    ["Registered", formatDate(doctor.createdAt)],
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <CheckCircle2 className="size-5 text-success" aria-hidden="true" />
            Approve doctor
          </DialogTitle>
          <DialogDescription>
            You are about to approve this doctor. Once approved, the doctor will immediately become
            visible in the HealNova patient application.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-xl border border-border bg-muted/40 p-4">
          <div className="flex items-center gap-3">
            <Avatar className="size-12 border border-border">
              {doctor.profilePicture && (
                <AvatarImage src={doctor.profilePicture} alt={doctor.name} />
              )}
              <AvatarFallback className="bg-primary text-sm font-semibold text-primary-foreground">
                {initials(doctor.name)}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <p className="truncate font-display text-base font-semibold">{doctor.name}</p>
              <p className="truncate text-xs text-muted-foreground">
                {doctor.email ?? "No email on file"}
              </p>
            </div>
            <DoctorStatusBadge status={doctor.status} className="ml-auto shrink-0" />
          </div>

          <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3 border-t border-border pt-4">
            {rows.map(([label, value]) => (
              <div key={label}>
                <dt className="text-[0.7rem] font-semibold tracking-wider text-muted-foreground uppercase">
                  {label}
                </dt>
                <dd className="mt-0.5 truncate text-sm">{value}</dd>
              </div>
            ))}
          </dl>
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={pending}>
            Cancel
          </Button>
          <Button onClick={onConfirm} disabled={pending}>
            {pending && <Loader2 className="size-4 animate-spin" />}
            Approve Doctor
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
